import { useCallback, useEffect, useState } from 'react';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import car from '~/assets/icons/car.svg';
import play from '~/assets/icons/play.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, VehicleType } from '~/shared';
import { usePlayerLevel } from '../../hooks';
import {
	JobOwnedVehicleItem,
	JobRentVehicleItem,
	setActiveStage,
	setVehicleList,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Car } from '../Car';
import { JobsStage } from '../JobsPanel/JobsPanel.model';
import { MenuTabs } from '../MenuTabs';
import {
	StyledButtonContent,
	StyledButtonsContainer,
	StyledCarsList,
	StyledCarsListHead,
} from './SelectVehicle.styled';

const SelectVehicle = () => {
	const dispatch = useLayoutDispatch();
	const ownedVehicleList = useObservable(rage.vehicles.getPlayerVehicleList());
	const rentVehicleList = useObservable(rage.jobs.getRentVehicles());
	const allowedVehicleTypes = useObservable(rage.jobs.getAllowedVehicleTypes());
	const [activeVehicleList, setActiveVehicleList] = useState<
		(JobOwnedVehicleItem | JobRentVehicleItem)[]
	>([]);
	const tabClass = useLayoutSelector((state) => state.modal.vehicleTabClass);
	const vehicleList = useLayoutSelector((state) => state.modal.vehicleList);
	const selectedVehicle = useLayoutSelector(
		(state) => state.modal.selectedVehicle,
	);
	const tabIndex = useLayoutSelector((state) => state.modal.vehicleTabIndex);

	const { playerLevel } = usePlayerLevel();

	useEffect(() => {
		const filteredOwnedVehicles =
			allowedVehicleTypes.length > 0
				? ownedVehicleList.filter((v) =>
						allowedVehicleTypes.includes(v.type as VehicleType),
				  )
				: ownedVehicleList;

		const ownedVehicles: JobOwnedVehicleItem[] = filteredOwnedVehicles.map(
			(vehicle) => ({
				id: vehicle.id,
				model: vehicle.model,
				name: vehicle.name,
				owned: true,
				enabled: true,
			}),
		);

		const rentVehicles: JobRentVehicleItem[] = rentVehicleList.map(
			(vehicle) => ({
				model: vehicle.vehicleModel,
				name:
					rage.vehicles.getVehicleNameByModel(vehicle.vehicleModel) ?? 'N/A',
				owned: false,
				enabled: vehicle.minLevel <= playerLevel,
				level: vehicle.minLevel,
				price: vehicle.price,
				priceCurrency: vehicle.priceCurrency,
				class: vehicle.vehicleClass,
			}),
		);

		dispatch(setVehicleList([...ownedVehicles, ...rentVehicles]));
	}, [ownedVehicleList, rentVehicleList, playerLevel, allowedVehicleTypes]);

	useEffect(() => {
		if (tabClass === 'owned') {
			setActiveVehicleList(vehicleList.filter((e) => e.owned));
			return;
		}

		setActiveVehicleList(
			(vehicleList as JobRentVehicleItem[]).filter((e) => e.class === tabClass),
		);
	}, [vehicleList, tabClass]);

	const startShift = useCallback(() => {
		if (!selectedVehicle) {
			return;
		}

		if (selectedVehicle.owned) {
			rage.jobs
				.startWorkShift({
					modelOrId: selectedVehicle.id,
					owned: true,
				})
				.catch((err) => rage.notifications.showException(err));
			return;
		}

		rage.jobs
			.startWorkShift({
				modelOrId: selectedVehicle.model,
				owned: false,
			})
			.catch((err) => rage.notifications.showException(err));
	}, [selectedVehicle]);

	useInputAction(InputAction.CommonMenuAccept, startShift);

	return (
		<div>
			<MenuTabs />
			<StyledCarsListHead>
				<SectionHeading text="Доступные автомобили" icon={car} />
				<Text style={{ fontSize: '16px' }}>
					<span style={{ color: 'rgba(255, 255, 255, 0.25)', fontWeight: 400 }}>
						Ваш уровень:&nbsp;
					</span>
					<span style={{ color: '#1782FF', fontWeight: 700 }}>
						{playerLevel}
					</span>
					<span style={{ color: '#1782FF', fontWeight: 400 }}>&nbsp;ур.</span>
				</Text>
			</StyledCarsListHead>
			<StyledCarsList tabIndex={-1}>
				{activeVehicleList.map((item, index) => (
					<Car {...item} key={`${item.model}-${index}-${tabIndex}`} />
				))}
			</StyledCarsList>
			<StyledButtonsContainer>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height60}
					style={{ fontSize: 16, fontWeight: 500 }}
					onClick={() => dispatch(setActiveStage(JobsStage.MAIN))}
				>
					<StyledButtonContent style={{ gap: 20 }}>
						<img src={arrowLeft} alt="arrowLeft" width={10} height={20} />
						<Text>Вернуться назад</Text>
					</StyledButtonContent>
				</Button>
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height60}
					style={{ fontSize: 16, fontWeight: 500 }}
					disabled={!selectedVehicle}
					onClick={startShift}
				>
					<StyledButtonContent style={{ gap: 10 }}>
						{selectedVehicle && (
							<img src={play} alt="play" width={20} height={20} />
						)}
						<Text>
							{selectedVehicle ? 'Начать смену' : 'Выберите автомобиль'}
						</Text>
					</StyledButtonContent>
				</Button>
			</StyledButtonsContainer>
		</div>
	);
};

export { SelectVehicle };
